import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AlertCircle,
  Building2,
  CheckCircle,
  Copy,
  ImageIcon,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import imageService from "@/services/imageService";
import { useAuth } from "@/hooks/useAuth";
import { showError, showInfo } from "@/store/errorStore";

const MAX_FILE_SIZE_MB = 5;

const PREVIEW_FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='160'><rect width='100%' height='100%' fill='#f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#9ca3af' font-family='Arial' font-size='14'>Preview indisponível</text></svg>`,
  );

function sanitizeBaseName(name) {
  return name
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9._-]/g, "");
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Biblioteca de imagens da empresa (GET/POST/DELETE /images).
 * O backend grava e lista só as imagens da empresa do token; todos os
 * usuários da mesma empresa veem as mesmas imagens.
 * Owner global precisa escolher a empresa no topo do painel antes de enviar.
 */
export default function UploadsManagementModal({ onSelect }) {
  const { companyCode, companyName, isGlobalOwner } = useAuth();
  // Owner global em "todas as empresas": lista tudo, mas não envia
  const canUpload = !!companyCode;

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // upload dialog
  const [openUpload, setOpenUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageName, setImageName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // delete confirm
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchList();
  }, []);

  // Libera a URL de pré-visualização do arquivo selecionado
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  async function fetchList() {
    setLoading(true);
    try {
      const images = await imageService.getAll();
      setRows([...images].sort((a, b) => (b.id || 0) - (a.id || 0)));
    } catch (error) {
      // Modal global já exibido pelo interceptor
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.url]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(q)),
    );
  }, [rows, search]);

  function resetUploadForm() {
    setFile(null);
    setImageName("");
    setUploadProgress(0);
    setUploadSuccess(false);
    setUploading(false);
  }

  function onChangeFile(e) {
    setUploadSuccess(false);
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }

    const validationError = imageService.validateFile(f);
    if (validationError) {
      showError(validationError, "Arquivo Inválido");
      e.target.value = null;
      return;
    }

    setFile(f);
    if (!imageName) {
      setImageName(sanitizeBaseName(f.name.replace(/\.[^.]+$/, "")));
    }
  }

  async function doUpload(e) {
    e.preventDefault();
    setUploadSuccess(false);

    if (!canUpload) {
      showError(
        "Escolha uma empresa no topo do painel para enviar imagens.",
        "Upload indisponível",
      );
      return;
    }
    if (!file) {
      showError("Selecione um arquivo.", "Upload");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const baseName = sanitizeBaseName(imageName) || "imagem";
      // company_code só é respeitado para o owner (escolha do topo do painel)
      await imageService.upload(
        file,
        baseName.slice(0, 120),
        setUploadProgress,
        isGlobalOwner ? companyCode : undefined,
      );

      setUploadSuccess(true);
      showInfo(`Imagem "${baseName}" enviada com sucesso!`, "Upload Concluído");

      setTimeout(async () => {
        setOpenUpload(false);
        resetUploadForm();
        await fetchList();
      }, 1200);
    } catch (error) {
      // Erros HTTP já abrem o modal global
      if (!error.response) {
        showError(error.message || "Falha no upload", "Erro no Upload");
      }
      setUploading(false);
      setUploadProgress(0);
    }
  }

  async function confirmDelete() {
    if (!toDelete) return;

    setDeleting(true);
    try {
      // O backend só remove imagens da empresa do usuário (senão 404)
      await imageService.delete(toDelete.id);
      showInfo(
        `Imagem "${toDelete.name}" removida com sucesso!`,
        "Imagem Removida",
      );
      await fetchList();
    } catch (error) {
      // Modal global já exibido pelo interceptor
    } finally {
      setDeleting(false);
      setToDelete(null);
    }
  }

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      showInfo("URL copiada para a área de transferência", "Copiado");
    } catch {
      showError("Não foi possível copiar a URL", "Erro");
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Uploads de Imagens
          </h1>
          <p className="text-gray-500 mt-1 flex flex-wrap items-center gap-2">
            Biblioteca de imagens da empresa
            {canUpload ? (
              <Badge variant="outline" className="font-medium">
                <Building2 className="w-3 h-3 mr-1" />
                {companyName || companyCode}
              </Badge>
            ) : (
              <Badge variant="outline" className="font-medium">
                Todas as empresas
              </Badge>
            )}
          </p>
        </div>
        <Button
          onClick={() => setOpenUpload(true)}
          disabled={!canUpload}
          title={
            canUpload
              ? undefined
              : "Escolha uma empresa no topo do painel para enviar imagens"
          }
          className="bg-[#1b5e20] hover:bg-[#144919] text-white"
        >
          <Upload className="w-4 h-4 mr-2" />
          Enviar Imagem
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Imagens da empresa</p>
            <p className="text-2xl font-bold text-gray-900">{rows.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar Imagens</CardTitle>
          <CardDescription>Pesquise pelo nome da imagem</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Digite o nome da imagem..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando imagens...</p>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed rounded-lg text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
          <p className="text-gray-600 font-medium">
            {search
              ? "Nenhuma imagem encontrada"
              : "Nenhuma imagem cadastrada"}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {search
              ? "Tente outro termo de busca"
              : "Clique em 'Enviar Imagem' para começar"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {filtered.map((item) => (
            <Card
              key={item.id}
              className="overflow-hidden hover:shadow-lg transition-shadow"
            >
              <CardContent className="p-0">
                <img
                  src={item.url}
                  alt={item.name}
                  className="w-full h-40 object-cover bg-gray-50 cursor-pointer hover:opacity-90 transition-opacity"
                  loading="lazy"
                  onClick={() => window.open(item.url, "_blank")}
                  onError={(e) => {
                    e.currentTarget.src = PREVIEW_FALLBACK;
                  }}
                />
                <div className="p-3 space-y-2">
                  <div
                    className="text-sm font-semibold truncate"
                    title={item.name}
                  >
                    {item.name}
                  </div>
                  <div className="text-xs text-gray-500 truncate" title={item.url}>
                    #{item.id}
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(item.url)}
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copiar URL
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setToDelete(item)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Excluir
                    </Button>
                  </div>

                  {onSelect && (
                    <Button
                      size="sm"
                      className="w-full"
                      style={{ background: "#1b5e20" }}
                      onClick={() => {
                        onSelect(item.url, item.name);
                        showInfo("Imagem selecionada", "Sucesso");
                      }}
                    >
                      Selecionar Imagem
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload */}
      <Dialog
        open={openUpload}
        onOpenChange={(open) => {
          if (uploading) return;
          setOpenUpload(open);
          if (!open) resetUploadForm();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#1b5e20]" />
              Enviar Imagem
            </DialogTitle>
            <DialogDescription>
              JPG, JPEG, PNG ou WEBP (até {MAX_FILE_SIZE_MB}MB). A imagem fica
              disponível para todos os usuários da empresa{" "}
              <strong>{companyName || companyCode}</strong>.
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={doUpload}>
            <div className="space-y-2">
              <Label htmlFor="upload-file">
                Arquivo <span className="text-red-500">*</span>
              </Label>
              <Input
                id="upload-file"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={onChangeFile}
                disabled={uploading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload-name">
                Nome da imagem (opcional, sem extensão)
              </Label>
              <Input
                id="upload-name"
                placeholder="ex.: acai-200ml"
                value={imageName}
                onChange={(e) => setImageName(e.target.value)}
                disabled={uploading}
              />
              <p className="text-xs text-gray-500">
                O nome é ajustado automaticamente (minúsculas, sem acentos e com
                hífens). Até 120 caracteres.
              </p>
            </div>

            {file && previewUrl && (
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                <img
                  src={previewUrl}
                  alt="Pré-visualização"
                  className="w-20 h-20 object-cover rounded-md border"
                />
                <div className="text-sm text-gray-600 flex-1 min-w-0">
                  <div className="font-medium truncate">{file.name}</div>
                  <div className="text-xs">{formatSize(file.size)}</div>
                </div>
              </div>
            )}

            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Enviando...</span>
                  <span className="font-medium">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}

            {uploadSuccess && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700 font-medium">
                  Upload concluído com sucesso!
                </span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpenUpload(false);
                  resetUploadForm();
                }}
                disabled={uploading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!file || uploading}
                className="bg-[#1b5e20] hover:bg-[#144919] text-white"
              >
                {uploading ? "Enviando..." : "Enviar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Exclusão */}
      <AlertDialog
        open={!!toDelete}
        onOpenChange={(open) => !open && !deleting && setToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir imagem?</AlertDialogTitle>
            <AlertDialogDescription>
              A imagem <strong>{toDelete?.name}</strong> será removida
              permanentemente. Produtos ou categorias que usam esta URL ficarão
              sem imagem.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
